/*
 * INCREMENTO 14/16 · TABLERO · REGLAS DE PRESENTACIÓN Y AGREGACIÓN, SIN UI.
 *
 * Todo lo que aquí vive es FUNCIÓN PURA: entra la respuesta del servidor, sale lo
 * que la pantalla pinta. Ninguna llamada de red, ningún estado, ningún componente.
 * Es lo que permite al Tester probar las reglas sin montar el Tablero entero.
 *
 * REGLA DURA DE ESTE MÓDULO (RN-600, RNF-053): EL TABLERO NO RECALCULA NADA.
 * No suma importes, no deduce estados, no reordena listas y no convierte monedas.
 * Cada cifra sale de la MISMA operación contratada que alimenta su pantalla de
 * origen, y si el Tablero mostrara un número distinto del de esa pantalla sería
 * defecto, no matiz.
 *
 * LA ÚNICA ARITMÉTICA PERMITIDA, y está escrita como excepción en RN-603: la
 * Diferencia del resumen del mes (Ingresos − Gastos de un mismo bloque de moneda).
 * Se hace UNA sola vez, EN CENTAVOS ENTEROS y sobre las cadenas decimales exactas
 * que mandó el servidor (RNF-001, RNF-033): nunca con números de punto flotante,
 * que es como se pierde un centavo.
 */

/** Los doce meses en minúscula, como los escribe el prototipo aprobado. */
export const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** Lo que se pinta cuando no hay cifra que pintar (mismo valor del núcleo). */
export const SIN_CIFRA = '—';

// ---------------------------------------------------------------------------
// Aritmética de importes en centavos enteros
// ---------------------------------------------------------------------------

/**
 * Cadena decimal → centavos enteros. Devuelve null si el valor no es un importe,
 * y null NO es cero: quien llama decide qué hacer con el dato ausente.
 */
export function aCentavos(valor) {
  if (valor === null || valor === undefined || valor === '') return null;

  const texto = String(valor).trim();
  const partes = /^(-|−)?(\d+)(?:\.(\d{1,2}))?$/.exec(texto);
  if (!partes) return null;

  const negativo = Boolean(partes[1]);
  const decimales = (partes[3] ?? '').padEnd(2, '0');
  const centavos = Number(partes[2]) * 100 + Number(decimales);
  return negativo ? -centavos : centavos;
}

/** Centavos enteros → cadena decimal de dos decimales, que es lo que el formato único espera. */
export function deCentavos(centavos) {
  const negativo = centavos < 0;
  const absoluto = Math.abs(centavos);
  const entero = Math.floor(absoluto / 100);
  const resto = String(absoluto % 100).padStart(2, '0');
  return `${negativo ? '-' : ''}${entero}.${resto}`;
}

/**
 * RN-603 · Diferencia = Ingresos − Gastos de un mismo bloque de moneda, restada
 * UNA sola vez y en centavos. Una cifra ausente cuenta como cero.
 */
export function diferenciaDelBloque(totalIngresos, totalGastos) {
  const ingresos = aCentavos(totalIngresos) ?? 0;
  const gastos = aCentavos(totalGastos) ?? 0;
  return deCentavos(ingresos - gastos);
}

/** Verdadero si la cadena decimal representa exactamente cero. */
export function esCero(valor) {
  const centavos = aCentavos(valor);
  return centavos !== null && centavos === 0;
}

/** Verdadero si la cadena decimal es negativa de verdad (−0.00 no lo es). */
export function esNegativo(valor) {
  const centavos = aCentavos(valor);
  return centavos !== null && centavos < 0;
}

// ---------------------------------------------------------------------------
// RN-599 · El periodo del Tablero: el mes calendario de la fecha LOCAL
// ---------------------------------------------------------------------------

/**
 * RN-599 · El Tablero no navega periodos y no tiene selector de mes: siempre es
 * el mes en curso del dispositivo. La fecha la resuelve el dispositivo y viaja
 * como `fechaLocal`; el servidor NUNCA la suple con su reloj (D-06, RN-086b).
 */
export function periodoDeFechaLocal(fechaDeNegocio) {
  const [anio, mes] = String(fechaDeNegocio).split('-');
  return Number(anio) * 100 + Number(mes);
}

/** Nombre del mes en minúscula: «octubre». */
export function nombreDelMes(fechaDeNegocio) {
  const [, mes] = String(fechaDeNegocio).split('-');
  return MESES[Number(mes) - 1] ?? '';
}

/** «Octubre 2026», con la inicial en mayúscula. */
export function etiquetaDeMesYAnio(fechaDeNegocio) {
  const [anio] = String(fechaDeNegocio).split('-');
  const mes = nombreDelMes(fechaDeNegocio);
  return `${mes.charAt(0).toUpperCase()}${mes.slice(1)} ${anio}`;
}

/** Último día del mes, por calendario y sin aproximar a 30 (RN-056b). */
export function ultimoDiaDelMes(fechaDeNegocio) {
  const [anio, mes] = String(fechaDeNegocio).split('-').map(Number);
  // Día 0 del mes siguiente = último del mes en curso, resuelto por el calendario.
  return new Date(anio, mes, 0).getDate();
}

/** Rango del mes en fechas de negocio AAAA-MM-DD. */
export function rangoDelMes(fechaDeNegocio) {
  const [anio, mes] = String(fechaDeNegocio).split('-');
  const ultimo = String(ultimoDiaDelMes(fechaDeNegocio)).padStart(2, '0');
  return { desde: `${anio}-${mes}-01`, hasta: `${anio}-${mes}-${ultimo}` };
}

/**
 * RF-209 · Subtítulo de periodo, literal del prototipo aprobado
 * (v5-tablero.html:117): «Octubre 2026 · periodo 202610 · del 01/10/2026 al 31/10/2026».
 */
export function subtituloDePeriodo(fechaDeNegocio) {
  const { desde, hasta } = rangoDelMes(fechaDeNegocio);
  const comoFecha = (f) => { const [a, m, d] = f.split('-'); return `${d}/${m}/${a}`; };
  return `${etiquetaDeMesYAnio(fechaDeNegocio)} · periodo ${periodoDeFechaLocal(fechaDeNegocio)} · `
    + `del ${comoFecha(desde)} al ${comoFecha(hasta)}`;
}

// ---------------------------------------------------------------------------
// T-1 · Resumen del mes (RN-601 a RN-604)
// ---------------------------------------------------------------------------

/** Rótulo del bloque de moneda, como lo dibuja el prototipo: «PESOS MEXICANOS · MXN». */
export const NOMBRE_DE_MONEDA = { MXN: 'Pesos mexicanos', USD: 'Dólares' };

export function rotuloDeMoneda(codigoISO) {
  return `${(NOMBRE_DE_MONEDA[codigoISO] ?? codigoISO).toUpperCase()} · ${codigoISO}`;
}

/**
 * RN-601, RN-602, RN-603 · Los bloques de «Resumen del mes», en el ORDEN en que
 * los entrega el servidor y SIN combinarlos jamás.
 *
 * De dónde salen las cifras: `totalesPorMoneda` de la MISMA llamada a
 * ConsultarMovimientos que alimenta el historial, con sus valores predeterminados.
 * Llegan ya resueltas con todas las reglas vigentes —cancelados fuera, eliminados
 * inexistentes, gastos netos de devoluciones, traspasos y pagos de tarjeta aparte,
 * y de una compra a meses solo la mensualidad imputada al mes (D-311)—. Aquí no se
 * vuelve a filtrar nada.
 *
 * LA CIFRA AUSENTE (RN-603): un tipo sin movimientos se pinta «—», no «$0.00», y
 * para la Diferencia cuenta como cero. El servidor entrega un importe, no un
 * conteo por tipo, así que «sin movimientos de ese tipo» se lee como importe en
 * cero —que es lo que dibuja el prototipo— y la Diferencia sale igual en los dos
 * casos. La cabecera sí distingue el cero verdadero (RN-612, ver `cifraDeGasto`).
 */
export function bloquesDelResumen(respuestaDeMovimientos) {
  const totales = respuestaDeMovimientos?.totalesPorMoneda ?? [];

  return totales.map((total) => ({
    idMoneda: total.idMoneda,
    codigoISO: total.codigoISO,
    rotulo: rotuloDeMoneda(total.codigoISO),
    ingresos: esCero(total.totalIngresos) ? null : total.totalIngresos,
    gastos: esCero(total.totalGastos) ? null : total.totalGastos,
    diferencia: diferenciaDelBloque(total.totalIngresos, total.totalGastos),
    cantidadMovimientos: total.cantidadMovimientos,
  }));
}

/**
 * RN-604 · Mes sin NINGÚN movimiento en ninguna moneda. La tarjeta no se oculta:
 * es el ancla del Tablero y muestra su mensaje con el acceso a capturar.
 */
export function elMesEstaVacio(respuestaDeMovimientos) {
  return (respuestaDeMovimientos?.totalesPorMoneda ?? []).length === 0;
}

// ---------------------------------------------------------------------------
// T-2 · Presupuesto del mes (RN-605 a RN-607)
// ---------------------------------------------------------------------------

/** Los dos estados que AVISAN. El servidor los produce; aquí solo se nombran. */
export const ESTADOS_QUE_AVISAN = Object.freeze(['CERCA_DEL_LIMITE', 'EXCEDIDO']);

/**
 * RN-606 (D-312) · Qué renglones lista T-2: SOLO los que avisan, en el ORDEN en
 * que el servidor los entrega (nombre ascendente, RN-225). Una categoría no se
 * promueve por estar «más excedida» y no hay tope de renglones: si avisan muchas,
 * se listan todas — ese día el aviso ES el contenido.
 *
 * Una línea de categoría desactivada que siga monitoreada entra igual si avisa
 * (RN-206): el aviso es del gasto, no del catálogo.
 *
 * Es filtro de PRESENTACIÓN sobre un estado que el servidor ya decidió: no es
 * lógica nueva, no es parámetro nuevo y no reabre el contrato del 5/6.
 */
export function lineasQueAvisan(respuestaDePresupuesto) {
  return (respuestaDePresupuesto?.lineas ?? [])
    .filter((linea) => ESTADOS_QUE_AVISAN.includes(linea.claveEstado));
}

/**
 * RN-607 · Los dos estados vacíos de T-2, que NO son el mismo caso:
 *   · SIN_PRESUPUESTO · el mes no tiene presupuesto definido → invita a definirlo.
 *   · TODO_EN_RANGO   · hay presupuesto y ninguna categoría avisa → lo confirma.
 *   · CON_AVISOS      · hay categorías que avisan → se listan.
 * En los tres casos la tarjeta SE MUESTRA: callar cuando todo va bien enseñaría
 * que la tarjeta solo existe para regañar.
 */
export const CASOS_DE_PRESUPUESTO = Object.freeze({
  SIN_PRESUPUESTO: 'SIN_PRESUPUESTO',
  TODO_EN_RANGO: 'TODO_EN_RANGO',
  CON_AVISOS: 'CON_AVISOS',
});

export function casoDePresupuesto(respuestaDePresupuesto) {
  const lineas = respuestaDePresupuesto?.lineas ?? [];
  if (lineas.length === 0) return CASOS_DE_PRESUPUESTO.SIN_PRESUPUESTO;
  return lineasQueAvisan(respuestaDePresupuesto).length > 0
    ? CASOS_DE_PRESUPUESTO.CON_AVISOS
    : CASOS_DE_PRESUPUESTO.TODO_EN_RANGO;
}

/** Ancho de la barra de avance: el porcentaje del servidor, tapado a 100 para dibujar. */
export function anchoDeBarra(porcentajeAvance) {
  const numero = Number(porcentajeAvance ?? 0);
  if (!Number.isFinite(numero) || numero < 0) return 0;
  return Math.min(numero, 100);
}

/** Clase de color de la barra, derivada del estado QUE YA DECIDIÓ EL SERVIDOR. */
export function claseDeBarra(claveEstado) {
  if (claveEstado === 'EXCEDIDO') return 'e';
  if (claveEstado === 'CERCA_DEL_LIMITE') return 'w';
  return '';
}

// ---------------------------------------------------------------------------
// T-4 · Últimos movimientos (RN-609, RN-610) y color por tipo (RN-614)
// ---------------------------------------------------------------------------

/** D-316 · TRES renglones, todos los tipos. Es el tamaño de página que se pide. */
export const RENGLONES_DE_ULTIMOS_MOVIMIENTOS = 3;

/**
 * RN-609 · Los renglones de T-4 son los primeros de la MISMA consulta de T-1: el
 * orden lo trae el servidor (`fecha DESC, idMovimiento DESC`, con el desempate de
 * renglones repartidos de RN-120a) y el cliente NO reordena ni recorta por tipo.
 * Un renglón de T-4 es EL MISMO renglón que encabeza el historial.
 */
export function renglonesRecientes(respuestaDeMovimientos) {
  return respuestaDeMovimientos?.movimientos ?? [];
}

/**
 * RN-610 · Mes sin renglones: la tarjeta NO se dibuja y no deja hueco, porque
 * T-1 ya informa el mes vacío con su mensaje y su acceso a capturar (RN-604).
 * Dos tarjetas diciendo «no hay nada» es una de más.
 */
export function debeDibujarseUltimosMovimientos(respuestaDeMovimientos) {
  return renglonesRecientes(respuestaDeMovimientos).length > 0;
}

/**
 * INSIGNIA DEL RENGLÓN por tipo de movimiento, como la dibuja el prototipo
 * aprobado. El icono y el color van por token y NUNCA son el único portador del
 * tipo: el tipo sigue escrito en el renglón y el signo dentro del importe
 * (RN-614, RNF-114). El icono lleva `aria-hidden` donde se usa.
 */
export const INSIGNIA_POR_TIPO = Object.freeze({
  GASTO: { icono: '▼', fondo: 'var(--eB)', borde: 'var(--eD)', texto: 'var(--err)' },
  INGRESO: { icono: '▲', fondo: 'var(--okB)', borde: 'var(--okD)', texto: 'var(--ok)' },
  TRASPASO: { icono: '⇄', fondo: 'var(--iB)', borde: 'var(--iD)', texto: 'var(--lnk)' },
  DEVOLUCION: { icono: '↩', fondo: 'var(--wB)', borde: 'var(--wD)', texto: 'var(--warn)' },
});

export function insigniaDelTipo(tipo) {
  return INSIGNIA_POR_TIPO[tipo] ?? INSIGNIA_POR_TIPO.TRASPASO;
}

// ---------------------------------------------------------------------------
// T-5 · Las tres cifras de cabecera (RN-611 a RN-613)
// ---------------------------------------------------------------------------

/**
 * RN-611 (D-310) · Saldo total de cuentas, UNA CIFRA POR MONEDA y nunca sumadas.
 *
 * Sale de `totalesPorMoneda.totalSaldos` de ConsultarCuentas: débito + ahorro +
 * efectivo de cuentas ACTIVAS únicamente (D-27, RN-072). LA DEUDA DE TARJETAS NO
 * SE RESTA (`totalDeudas` va aparte y nunca se combina, RF-041): este renglón es
 * «cuánto dinero tengo», no «cuánto tengo menos cuánto debo».
 *
 * Una moneda sin cuentas activas no muestra renglón; sin ninguna cuenta activa la
 * lista queda vacía y la cifra se pinta «—». No es cifra de periodo: es el saldo
 * vigente al consultar.
 *
 * ===========================================================================
 * CORREGIDO · FIX de producción del 2026-10-08 · CAUSA REAL DEL DEFECTO D-322/§3
 * ===========================================================================
 * Esta función leía `respuestaDeCuentas.bloques`, UN CAMPO QUE EL CONTRATO NO
 * TIENE. `ConsultarCuentas` responde `{ cuentas, totalesPorMoneda, versionCuentas,
 * cantidadDesactivadas, advertencias }` (RespuestaDeCuentasDto, contratos-cuentas
 * §ConsultarCuentas): la lista viene PLANA y es la pantalla de Cuentas la que
 * agrupa por moneda para dibujar. Así que `bloques` salía `undefined`, el `?? []`
 * lo convertía en lista vacía, y la cabecera leía esa lista vacía como «no hay
 * ninguna cuenta activa» — con 15 cuentas en la respuesta.
 *
 * AHORA SE LEE `totalesPorMoneda`, QUE ES LA FUENTE EXACTA DE RN-611: el servidor
 * lo arma agrupando SOLO las cuentas ACTIVAS (ArmarTotales, D-27, RN-072d), así que
 * una moneda sin cuentas activas ya no aparece ahí y no hace falta ningún filtro
 * propio. Menos aritmética de cliente, no más.
 *
 * Y SE FUE EL `?? '0.00'`: un total que no llegó no es cero (RN-624). Si no hay
 * total para una moneda, no se inventa un renglón en cero — el lector de
 * `estadosHonestos.js` manda esa respuesta al estado de error.
 */
export function saldosPorMoneda(respuestaDeCuentas) {
  const totales = respuestaDeCuentas?.totalesPorMoneda ?? [];

  return totales
    .filter((total) => total?.totalSaldos !== null && total?.totalSaldos !== undefined)
    .map((total) => ({
      idMoneda: total.idMoneda,
      codigoISO: total.codigoISO,
      totalSaldos: total.totalSaldos,
    }));
}

/**
 * RN-612 · «Gasto de <mes>» es EXACTAMENTE la cifra «Gastos» de T-1, por moneda,
 * sin segunda fuente ni segunda llamada. Aquí sí vale el cero verdadero: una
 * moneda con movimientos y sin gastos muestra «$0.00» con su código, porque el
 * cero es un dato, no una ausencia. Un mes sin movimientos deja la lista vacía y
 * la cabecera pinta «—» (coherente con RN-603).
 */
export function cifraDeGasto(respuestaDeMovimientos) {
  return (respuestaDeMovimientos?.totalesPorMoneda ?? []).map((total) => ({
    idMoneda: total.idMoneda,
    codigoISO: total.codigoISO,
    totalGastos: total.totalGastos,
  }));
}

/**
 * RN-613 · «Disponible del presupuesto» es `totalDisponible` tal cual, ya resuelto
 * con la resta completa de RN-228 (presupuestado − gastado − gasto sin presupuesto).
 * PUEDE SER NEGATIVO y se muestra con su signo y tono de alerta, sin recortarse a
 * cero (D-146). Solo MXN. Sin presupuesto definido en el mes: «—», no «$0.00»,
 * que afirmaría «te queda nada».
 */
export function cifraDeDisponible(respuestaDePresupuesto) {
  if (!respuestaDePresupuesto) return null;
  if (casoDePresupuesto(respuestaDePresupuesto) === CASOS_DE_PRESUPUESTO.SIN_PRESUPUESTO) return null;
  return respuestaDePresupuesto.totalDisponible ?? null;
}

// ---------------------------------------------------------------------------
// F-01 · Subtítulos vivos de Ajustes › Datos del hogar (RN-615, D-315)
// ---------------------------------------------------------------------------

/**
 * RN-615 · N = cuentas activas que NO son de crédito; M = tarjetas de crédito
 * activas. Se cuentan de la respuesta de ConsultarCuentas con sus valores
 * predeterminados, sin una consulta extra.
 *
 * CORREGIDO · FIX del 2026-10-08. Tenía EL MISMO defecto que `saldosPorMoneda`:
 * leía `bloques`, que el contrato no tiene, así que el subtítulo de «Datos del
 * hogar» de Ajustes contaba 0 cuentas y 0 tarjetas teniendo 15. Es la misma causa
 * y se corrige en la misma vuelta: dejar un lector roto porque su síntoma no se
 * reportó es dejar el defecto esperando.
 */
export function conteoDeCuentasYTarjetas(respuestaDeCuentas) {
  const cuentas = respuestaDeCuentas?.cuentas ?? [];
  const activas = cuentas.filter((cuenta) => cuenta.activo);

  return {
    cuentas: activas.filter((cuenta) => !cuenta.esCredito).length,
    tarjetas: activas.filter((cuenta) => cuenta.esCredito).length,
  };
}

/** Singular y plural correctos: «1 cuenta · 1 tarjeta de crédito». */
export function subtituloDeCuentasYTarjetas({ cuentas, tarjetas }) {
  const enPlural = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;
  return `${enPlural(cuentas, 'cuenta', 'cuentas')} · `
    + `${enPlural(tarjetas, 'tarjeta de crédito', 'tarjetas de crédito')}`;
}

/** «Definir importe por categoría para octubre 2026» (mes de la fecha local). */
export function subtituloDePresupuestoDelMes(fechaDeNegocio) {
  const [anio] = String(fechaDeNegocio).split('-');
  return `Definir importe por categoría para ${nombreDelMes(fechaDeNegocio)} ${anio}`;
}
